import { getSite } from '@/utils/getSite';
import { BRANDS, SECTION } from 'lib/data/video/types';

export interface Tag {
  seoDescription?: string;
  seoTitle?: string;
  pageTitle?: string;
  description?: string;
}

type GetByTagName = (tagName: string) => string;

export interface TagTemplate {
  getSeoTitle?: GetByTagName;
  getSeoDescription?: GetByTagName;
  getPageTitle?: GetByTagName;
  getDescription?: GetByTagName;
}

export interface Config {
  frontPage: {
    sections: SECTION[];
    showsSectionTitle?: string;
    newsSectionTitle?: string;
    pageTitle?: string;
    trendingSectionTitle?: string;
    seoTitle?: string;
    seoDescription?: string;
    description?: string;
  };
  tagPage: TagTemplate & {
    tag?: Record<string, Tag>;
  };
  allShowPage?: {
    seoDescription?: string;
    seoTitle?: string;
    headline?: string;
    description?: string;
  };
  showsPage: {
    getShowDefaultDescription?: (programName: string) => string;
    getShowSeoDescription?: (programName: string) => string;
  };
}

export type TagPageConfig = Config['tagPage']['tag'];

const DEFAULT_CONFIG: Config = {
  frontPage: {
    sections: ['new', 'shows', 'tags', 'trending'],
  },
  tagPage: {},
  allShowPage: {
    headline: 'Våra program',
  },
  showsPage: {},
};

export const VIDEO_PAGES_CONFIG: Config = ({
  [BRANDS.SVENSKDAM]: {
    frontPage: {
      sections: ['new', 'shows', 'tags', 'trending'],
      pageTitle: 'De senaste videoklippen från Svensk dam',
      newsSectionTitle: 'SENASTE VIDEOS',
      showsSectionTitle: 'KUNGLIGA PROGRAM',
      trendingSectionTitle: 'MEST SEDDA',
      seoTitle: 'Se alla videos om kungligt & kändis på Svensk Damtidning',
      seoDescription:
        'Upptäck alla videos på Svensk Dam – från samtal med våra hovexperter till senaste nytt om kändisar och kungligheter världen över',
      description:
        'Se de senaste kungliga nyheterna och lär dig mer om den svenska nöjesvärlden på Svensk Damtidnings videosida. Här kan du titta på allt från intervjuer med våra hovexperter till spännande tillbakablickar och färska nyheter med svenska och internationella kungligheter i fokus. Upptäck våra programserier och titta på mängder av spännande innehåll här.',
    },
    tagPage: {
      getDescription: (programName: string) =>
        `Här kan du se alla intervjuer och videos om ${programName}. Hos Svensk Dam hittar du det och mycket mer.`,
    },
    allShowPage: {
      headline: 'Våra program',
      seoDescription:
        'Här hittar du alla program du kan se på Svensk Damtidning. Kanske vill du titta på En kunglig måltid där kungliga maträtter lagas av kockar eller varför inte Svensk Dam förklarar? Vi har såklart också program om både Nobel och Victoriadagen - här hittar du alla program på Svensk Dam!',
      seoTitle: 'Alla program på Svensk dam',
      description:
        'Här hittar du alla program du kan se på Svensk Damtidning. Kanske vill du titta på En kunglig måltid där kungliga maträtter lagas av kockar eller varför inte Svensk Dam förklarar? Vi har såklart också program om både Nobel och Victoriadagen - här hittar du alla program på Svensk Dam!',
    },
  },
  [BRANDS.HANT]: {
    frontPage: {
      sections: ['new', 'shows', 'tags', 'trending'],
      pageTitle: 'De senaste videoklippen från Hänt',
      newsSectionTitle: 'SENASTE VIDEOS',
      showsSectionTitle: 'Alla program',
      trendingSectionTitle: 'MEST SEDDA',
      seoTitle: 'Hänt | Video',
      seoDescription:
        'Se alla videos inom nöje och kungligt på Hänt – allt från intervjuer på röda mattan till nyheter om kändisar och kungligheter.',
      description:
        'På Hänt hittar du nöjesrelaterade och kungliga intervjuer, videos och nyheter! Se intervjuer från röda mattan och fantastiska ögonblick från när Hänt träffar Sveriges hetaste kändisar. Vi har samlat alla Hänts videoklipp här så du håller dig uppdaterad med det senaste och inte missar något från nöjesvärlden!',
    },
    tagPage: {
      getDescription: (tagName: string) =>
        `Här kan du se du alla intervjuer och videos om ${tagName}. På Hänt hittar du videos, klipp och intervjuer om det senaste inom nöje och kungligt.`,
    },
    allShowPage: {
      headline: 'Alla program på Hänt',
      seoDescription:
        'Här ser du alla Hänts program som bland annat Vad äter du när... och Röda mattan och självklart Exklusiva intervjuer!',
      seoTitle: 'Alla program på Hänt',
      description:
        'Här är alla program du kan se på Hänt! Vill du kanske titta på Röda mattan, Exklusiva intervjuer, eller Vad äter du när… där Hänt möter folkkära kändisar? Här hittar du alla program samlade på ett ställe!',
    },
    showsPage: {
      getShowDefaultDescription: (programName: string) =>
        `Här kan du se alla avsnitt av Hänts program ${programName}. Missa heller inte alla andra program och serier på Hänt!`,
      getShowSeoDescription: (programName: string) =>
        `Se alla avsnitt av ${programName} på Hänt. Och missa inte alla våra andra program och videos!`,
    },
  },
  [BRANDS.ELLE]: {
    frontPage: {
      sections: ['new', 'shows', 'tags', 'trending'],
      pageTitle: 'Alla videoklipp på ELLE',
      newsSectionTitle: 'SENASTE VIDEOS',
      showsSectionTitle: 'Alla program',
      trendingSectionTitle: 'MEST SEDDA',
      seoTitle: 'ELLE | Video',
      seoDescription:
        'Se alla videos inom mode, beauty, inredning, nöje och mat & vin på ELLE. Här hittar du även alla våra program! ',
      description:
        'Som en av Sveriges största plattformar för trendmedvetna kvinnor strävar vi på ELLE efter att inspirera inom ämnen som mode, beauty och inredning. Här kan du se alla ELLEs videoklipp – från programmet Min rutin med några av Sveriges mest älskade kändisar, till inspirerande Hemma hos-reportage och goda recept på drinkar och mat. Se och låt dig inspireras!',
    },
    tagPage: {
      getPageTitle: (tagName: string) => `Alla videos om ${tagName}`,
      getSeoDescription: (tagName?: string) =>
        'Se alla ELLEs videoklipp om Taggens namn. Det och mycket mer kan du titta på hos ELLE!',
      getDescription: (tagName: string) =>
        `Här kan du titta på alla våra videos och intervjuer om ${tagName}. Missa inte det eller alla våra andra videoklipp på ELLE! `,
      tag: {
        ['Beauty']: {
          seoDescription:
            'Här kan du se alla videos och intervjuer inom beauty och skönhet på ELLE. Det och mycket mer hittar du på ELLE!',
          seoTitle: 'Beauty | videos | ELLE',
          pageTitle: 'Alla videos om beauty och skönhet',
          description:
            'Här kan du se alla videos och intervjuer inom beauty på ELLE – vi har massor med inspirerande innehåll för dig som älskar skönhet. Du kan också se program som Min rutin där kända personer visar upp sina skönhetsrutiner eller Manikyrdrömmar där ELLEs skönhetsredaktör testar nageltrender. Det och mycket kan du se här!',
        },
        ['Inredning']: {
          seoDescription:
            'Här kan du se alla videos och intervjuer inom inredning på ELLE. Det och mycket mer hittar du på ELLE!',
          seoTitle: 'Inredning | videos | ELLE',
          pageTitle: 'Alla videos om inredning ',
          description:
            'Älskar du inredning? Då har du kommit rätt! Här kan du se alla videos och intervjuer inom inredning på ELLE. Du kan också se program som ELLE Kikar in där vi får en exklusiv inblick i Sveriges vackraste hem, eller Decodrömmar där inredningsstylisten Dennis Valencia hur du med enkla knep piffar upp hemmet . Det och mycket kan du se här!',
        },
        ['Mode']: {
          seoDescription:
            'Här kan du se alla videos och intervjuer inom mode på ELLE. Det och mycket mer hittar du på ELLE!',
          seoTitle: 'Mode | videos | ELLE',
          pageTitle: 'Alla videos om mode',
          description:
            'Här kan du se alla videos och intervjuer inom mode på ELLE – vi har massor med inspirerande innehåll för dig som älskar mode. Du kan också se program som ELLE Trend med ELLEs moderedaktör Emma Blomberg eller ELLE Stylar med Josefine Kanth. Det och mycket mer kan du se här!',
        },
        ['Nöje']: {
          seoDescription:
            'Här kan du se alla videos och intervjuer om nöje på ELLE. Det och mycket mer hittar du på ELLE!',
          seoTitle: 'Nöje | videos | ELLE ',
          pageTitle: 'Alla videos om nöje',
          description:
            'Här kan du se alla videos från ELLE om nöje. Vad är det senaste på sociala medier och vad är snackisen i Hollywood du inte får missa? Missa inte heller våra program som POV med Helle Schunnesson eller exklusiva kändisintervjuer från ELLE-galan. Detta och mycket mer kan du se här!',
        },
      },
    },
    allShowPage: {
      headline: 'Alla våra program på ELLE',
      seoDescription:
        'Här hittar du alla program och shower på ELLE. Allt från Min rutin och Hemma hos till inspirerande serier om mat, kungligt och mer! ',
      seoTitle: 'Våra program | ELLE ',
      description:
        'Här hittar du alla ELLEs program och shower inom ämnen som mode, beauty, mat, dryck, nöje och intredning samlade på ett och samma ställe. Vi har allt från Min rutin, POV, ELLE trend och ELLE Hemma hos till ELLE kungligt, Bröllop med Billgren, Middag med Wood samt ELLE pop. Titta och insireras! ',
    },
    showsPage: {
      getShowDefaultDescription: (programName: string) =>
        `Här kan du se alla avsnitt av ELLEs program ${programName}. Titta och inspireras – och missa inte alla våra andra serier och program.`,
      getShowSeoDescription: (programName: string) =>
        `Här kan du se alla avsnitt av Showens namn. Titta och inspireras samt missa inte alla våra andra program!`,
    },
  },
  [BRANDS.MABRA]: {
    frontPage: {
      sections: ['new', 'shows', 'tags', 'trending'],
      pageTitle: 'Alla videoklipp på MåBra',
      newsSectionTitle: 'Senaste videos',
      showsSectionTitle: 'Alla program',
      trendingSectionTitle: 'Mest sedda',
      seoTitle: 'Alla våra videoklipp – MåBra',
      seoDescription:
        'Här hittar du alla videoklipp på MåBra. Lär dig om hälsa genom intervjuer, inspirerande reportage och mer här.',
      description:
        'På denna sida hittar du alla videoklipp av MåBra. Vi på MåBra vill inspirera till mer välbefinnande i ditt liv genom att skapa värdefullt innehåll inom ämnen som hälsa, träning och psykologi. Se våra populära programserier och mängder av andra klipp här på MåBras alldeles egna videosida!',
    },
    tagPage: {
      getSeoDescription: (tagName: string) =>
        `Se alla videoklipp om ${tagName} på MåBra och hitta fler videoklipp här!`,
      getSeoTitle: (tagName: string) => `${tagName} – alla videoklipp på MåBra`,
      getPageTitle: (tagName: string) => `Alla videoklipp om ${tagName}`,
      getDescription: (tagName: string) =>
        `Här samlar vi alla videos om ämnet ${tagName}. Det och mycket mer hittar du hos MåBra – platsen för dig som vill lära dig mer om hälsa och välbefinnande.`,
    },
    allShowPage: {
      headline: 'Alla program på MåBra',
      seoDescription:
        'Här hittar du alla programserier på MåBra. Lär dig om hälsa genom intervjuer, inspirerande reportage och mer här.',
      seoTitle: 'Våra program och videoserier – MåBra',
      description:
        'Här hittar du alla program inom träning, hälsa och livsstil hos MåBra. Se till exempel "Min hälsa", där vi intervjuar psykiatriker Anders Hansen och psykolog Maria Farm om psyket eller "Löpskolan med Joanna Swica" där du får lära dig om bra löpteknik.',
    },
    showsPage: {
      getShowDefaultDescription: (programName: string) =>
        `Här kan du se alla avsnitt av MåBras ${programName}. Se programmen här och missa inte heller våra andra program inom hälsa, träning och välbefinnande på MåBra!`,
      getShowSeoDescription: (programName: string) =>
        `På denna sida kan du se alla avsnitt av ${programName}. Se det och fler hälsorelaterade program på MåBra!`,
    },
  },
  [BRANDS.ALLAS]: {
    frontPage: {
      sections: ['new', 'shows', 'tags', 'trending'],
      pageTitle: 'Alla videoklipp på Allas',
      newsSectionTitle: 'Senaste videos',
      showsSectionTitle: 'Alla program',
      trendingSectionTitle: 'Mest sedda',
      seoTitle: 'Videoklipp på Allas',
      seoDescription:
        'Se praktiska och inspirerande videoklipp från Allas. Lär dig om trädgård, hushåll, handarbete och mycket mer tillsammans med oss!',
      description:
        'Allas är platsen för dig som vill ha avkopplande innehåll som berör. På vår videosida kan du titta på videos om trägård, hushåll, handarbete, nostalgi och mycket mer. Titta till exempel på programmet Ekohemmet där du får smarta husmorsknep eller Snabbstickat där du kan lära dig olika enkla stickningstekniker. Se alla videos från Allas här!',
    },
    tagPage: {
      getSeoDescription: (tagName: string) =>
        `Här kan du se alla intervjuer och videos inom ämnet ${tagName}. Hos Allas hittar du det och mycket mer.`,
      getSeoTitle: (tagName: string) => `${tagName} – Alla videoklipp`,
      getPageTitle: (tagName: string) => `Alla videoklipp av ${tagName}`,
      getDescription: (tagName: string) =>
        `Här kan du se alla intervjuer och videos inom ämnet ${tagName}. Hos Allas hittar du det och mycket mer.`,
      tag: {
        ['Mat & Dryck']: {
          seoDescription:
            'Här kan du se alla intervjuer och videos om Mat & Dryck. Hos Allas hittar du det och mycket mer.',
          seoTitle: 'Mat & dryck – alla videoklipp på Allas',
          pageTitle: 'Alla videoklipp om mat & dryck',
          description:
            'Här kan du se alla intervjuer och videos inom ämnet Mat & Dryck. Se när Fredriks Fika lagar magiska recept eller få tips på hur du minskar matsvinn. Hos Allas hittar du det och mycket mer.',
        },
      },
    },
    allShowPage: {
      headline: 'Alla program på Allas videosida',
      seoDescription:
        'Här hittar du alla programserier på Allas – allt från snabbstickat till vinterträdgården och andra program. Se alla program här!',
      seoTitle: 'Program – alla vidoserier på Allas',
      description:
        'Här kan du kolla på alla program på Allas på ett och samma ställe! Se till exempel Snabbstickat för dig som är pysslig gillar att sticka, Vinterträdgården för dig som har gröna fingrar eller Mauds matknep för dig som är intresserad av mat. Se dessa program och fler här på Allas! ',
    },
  },
}[getSite()] ?? DEFAULT_CONFIG) as Config;
